import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import QuoteForm from "../components/QuoteForm";
import ClientMarquee from "../components/ClientMarquee";
import SEO from "../components/SEO";
import { serviceJsonLd } from "../data/seo";
import { Camera, Film, Sparkles, Plane, Image as ImageIcon, Scissors } from "lucide-react";

const SERVICES = [
  {
    icon: Film,
    label: "Video Production",
    sub: "Filming, editing & motion graphics",
    img: "/thumbs/646009344.jpg",
  },
  {
    icon: Sparkles,
    label: "Animation / Motion Graphics",
    sub: "High-quality computer-generated imagery",
    img: "/thumbs/468864496.jpg",
  },
  {
    icon: Camera,
    label: "Photography",
    sub: "Organisation, shoots & photo editing",
    img: "/portfolio/beauty-product-photography-liverpool-9.jpg",
  },
  {
    icon: Plane,
    label: "Drone Footage",
    sub: "Licensed pilots for stills & filming",
    img: "/thumbs/665944961.jpg",
  },
  {
    icon: Scissors,
    label: "Video Editing",
    sub: "Editing your footage into multiple formats",
    img: "/thumbs/286504818.jpg",
  },
  {
    icon: ImageIcon,
    label: "Photo Editing",
    sub: "Editing and stylising your images",
    img: "/portfolio/product-photography-liverpool-10.jpg",
  },
];

const TESTIMONIALS = [
  {
    quote: "Brilliant company to work with. Highly recommended.",
    author: "Refinery Group",
  },
  {
    quote:
      "Yellow are amazing! All I needed to do was write a brief and they did all the hard work and made everything so easy. The final result exceeded what I could have ever imagined. Anytime I need a video creating in the future, I know exactly who to call.",
    author: "Happy Creative",
  },
];

export default function WhiteLabel() {
  return (
    <main className="pt-28 pb-12" data-testid="white-label-page">
      <SEO
        title="White Label Video Production Liverpool | For Agencies | Yellow"
        description="White label video production for marketing agencies. Invisible crew, NDA-friendly, delivered under your brand. Filming, editing, animation, drone & photography from a Liverpool studio."
        path="/white-label"
        keywords="white label video production liverpool, white label video production, video production for agencies liverpool, agency video production partner, white label videography, freelance video production liverpool, video production outsourcing liverpool"
        jsonLd={serviceJsonLd({
          name: "White Label Video Production Liverpool",
          description:
            "White label video production, animation, photography and drone services for marketing agencies, delivered under your brand. Based in Liverpool, UK wide.",
          path: "/white-label",
        })}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "White Label", path: "/white-label" },
        ]}
      />
      <section className="yv-container">
        <Reveal>
          <p className="yv-eyebrow">White Label Video Production · For Agencies</p>
          <h1 className="yv-h1 mt-3 max-w-5xl">
            <span style={{ color: "var(--yellow)" }}>White label video production:</span> Helping agencies deliver more work, win bigger clients and grow profits.
          </h1>
          <p className="text-white/70 max-w-3xl mt-6 text-lg leading-relaxed">
            Need a <strong className="text-white">white label video production partner</strong>? Yellow gives you instant access to our video production crew, photographers, editors, animators and drone pilots, all delivered under your brand, NDA-friendly.
          </p>
          <div className="flex gap-3 mt-8">
            <Link to="/contact" className="yv-btn yv-btn-primary">Discuss a project</Link>
          </div>
        </Reveal>
      </section>

      <ClientMarquee />

      <section className="yv-container !mt-16 grid md:grid-cols-2 gap-10 items-center">
        <Reveal>
          <h2 className="yv-h2 text-white mb-4">
            White-label support for agencies ready to scale.
          </h2>
          <p className="text-white/70 leading-relaxed mb-4">
            Running an agency shouldn't mean saying no to good work, overloading your team or sacrificing profit just to keep clients happy. If you're struggling with delivery capacity, missing in-house skills or just need extra hands, Yellow becomes your invisible team, working behind the scenes under your brand.
          </p>
          <p className="text-white/70 leading-relaxed">
            Instant access to expert camera operators, editors, animators, drone pilots and photographers who have collectively completed over 1,000 projects, without the cost, commitment or risk of hiring internally or buying high-end equipment.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <div className="aspect-video relative rounded-md overflow-hidden border border-white/10">
            <img
              src="/portfolio/corporate-photographer-liverpool-16.jpg"
              alt="White Label Production"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-transparent" />
          </div>
        </Reveal>
      </section>

      <section className="yv-container !mt-20">
        <Reveal>
          <p className="yv-eyebrow">What you get</p>
          <h2 className="yv-h2 text-white mt-2 mb-10">Capabilities you can resell as your own.</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.label} delay={i * 60}>
                <article className="yv-service-tile aspect-[4/3]" data-testid={`wl-service-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`}>
                  <img src={s.img} alt={s.label} loading="lazy" />
                  <div className="yv-tile-meta">
                    <div className="flex items-center gap-2 mb-1 text-[var(--yellow)]">
                      <Icon size={18} />
                      <p className="text-white font-bold text-base">{s.label}</p>
                    </div>
                    <p className="text-white/60 text-xs">{s.sub}</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
        <p className="text-white/50 text-sm mt-10 text-center max-w-2xl mx-auto">
          All projects include multiple revisions and unlimited copyright usage of final exported files, we're also happy to sign NDAs so everything is confidential while we work with you.
        </p>
      </section>

      <section className="yv-container !mt-20">
        <Reveal>
          <p className="yv-eyebrow">Agency testimonials</p>
          <h2 className="yv-h2 text-white mt-2 mb-10">From agencies who trust us behind the scenes.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <blockquote key={t.author} className="p-8 bg-white/[0.03] border border-white/10 rounded-md" data-testid={`wl-testimonial-${i}`}>
              <p className="yv-quote">"{t.quote}"</p>
              <footer className="mt-5">
                <p className="text-white font-bold text-sm">{t.author}</p>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="yv-container !mt-20 max-w-2xl mx-auto">
        <Reveal>
          <QuoteForm heading="Discuss your project" />
        </Reveal>
      </section>
    </main>
  );
}
